import React from "react";
import {
    Bar,
    BarChart,
    CartesianGrid,
    Line,
    LineChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
    ComposedChart,
} from "recharts";

interface MixChartProps {
    data?: any[];
}

const MixChart: React.FC<MixChartProps> = ({ data = [] }) => {
    const months = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
    ];

    const chartData = data.map((item: any) => ({
        month: months[item.month - 1],
        completed: Number(item.complianceCount ?? 0),
        overdue: Number(item.overdueCount ?? 0),
        failed: Number(item.failureCount ?? 0),
    }));

    const maxValue = Math.max(
        ...data.map((item: any) =>
            Math.max(
                Number(item.complianceCount ?? 0),
                Number(item.overdueCount ?? 0),
                Number(item.failureCount ?? 0)
            )
        ),
        5
    );

    const primaryColor = "var(--primary-color)";
    const overdueColor = "#FFBF44";
    const failedColor = "#BF613B";

    /**
     * Custom tooltip
     */
    const CustomTooltip = ({
        active,
        payload,
        label,
    }: any) => {
        if (!active || !payload?.length) {
            return null;
        }

        return (
            <div
                style={{
                    background: "#fff",
                    border: "1px solid #ddd5c9",
                    borderRadius: 6,
                    padding: "8px 12px",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                }}
            >
                <div
                    style={{
                        fontWeight: 600,
                        marginBottom: 6,
                        color: "#333",
                    }}
                >
                    {label}
                </div>

                {payload.map((item: any) => (
                    <div
                        key={item.dataKey}
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 7,
                            fontSize: 13,
                            marginTop: 3,
                        }}
                    >
                        <span
                            style={{
                                width: 8,
                                height: 8,
                                borderRadius: "50%",
                                background: item.color,
                                display: "inline-block",
                            }}
                        />

                        <span style={{ color: "#555" }}>
                            {item.name}:
                        </span>

                        <strong style={{ color: "#222" }}>
                            {item.value}
                        </strong>
                    </div>
                ))}
            </div>
        );
    };

    return (
        <ResponsiveContainer width="100%" height={350}>
            <ComposedChart
                data={chartData}
                margin={{
                    top: 10,
                    right: 10,
                    left: 0,
                    bottom: 10,
                }}
            >
                {/* Dashed horizontal grid */}
                <CartesianGrid
                    stroke="#DDD5C9"
                    strokeDasharray="4 4"
                    vertical={false}
                />

                {/* X Axis */}
                <XAxis
                    dataKey="month"
                    axisLine={false}
                    tickLine={false}
                    tick={{
                        fill: "#555",
                        fontSize: 14,
                    }}
                />

                {/* Y Axis */}
                <YAxis
                    domain={[0, maxValue]}
                    tickCount={6}
                    axisLine={false}
                    tickLine={false}
                    tick={{
                        fill: "#555",
                        fontSize: 12,
                    }}
                    allowDecimals={false}
                />

                {/* Shared tooltip */}
                <Tooltip
                    content={<CustomTooltip />}
                    cursor={false}
                />

                {/* Completed - Column */}
                <Bar
                    dataKey="completed"
                    name="Completed"
                    fill={primaryColor}
                    barSize={42}
                    radius={[5, 5, 0, 0]}
                />

                {/* Overdue - Line */}
                <Line
                    type="linear"
                    dataKey="overdue"
                    name="Overdue"
                    stroke={overdueColor}
                    strokeWidth={3}
                    dot={{
                        r: 4,
                        strokeWidth: 0,
                        fill: overdueColor,
                    }}
                    activeDot={{
                        r: 6,
                    }}
                />

                {/* Failed - Line */}
                <Line
                    type="linear"
                    dataKey="failed"
                    name="Failed"
                    stroke={failedColor}
                    strokeWidth={3}
                    dot={{
                        r: 4,
                        strokeWidth: 0,
                        fill: failedColor,
                    }}
                    activeDot={{
                        r: 6,
                    }}
                />
            </ComposedChart>
        </ResponsiveContainer>
    );
};

export default MixChart;